import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import Swal from 'sweetalert2';
import './SlotGame3.css';
import SlotgameOpen from './assets/Slotgame-open.mp3';
import SlotTurn from './assets/slot-turn.mp3';
import CountdownSound from './assets/Countdown-5s.wav';
import TrueSound from './assets/true.wav';
import WrongSound from './assets/wrong.wav';
import { db } from '../Firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

// สัญลักษณ์สำหรับแต่ละช่อง
const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const OPERATORS = ['×'];

// ฟังก์ชันสำหรับสร้าง Reel แบบสุ่ม
const generateReelSymbols = (finalSymbol, symbolType) => {
  const reel = [];
  const operators = ['×'];
  
  // สร้างสัญลักษณ์แบบสุ่ม 30 ตัวเพื่อเป็นตัวหมุนผ่าน
  for (let i = 0; i < 30; i++) {
    if (symbolType === 'number') {
      // สุ่มเลข 1-9
      reel.push(String(Math.floor(Math.random() * 9) + 1));
    } else {
      reel.push(operators[Math.floor(Math.random() * operators.length)]);
    }
  }
  // ใส่สัญลักษณ์ที่เป็นผลลัพธ์จริงไว้ตัวสุดท้าย
  reel.push(finalSymbol);
  return reel;
};

// ฟังก์ชันสำหรับสร้างตัวเลขที่เหมาะสมตาม operator
const generateAppropriateNumbers = (operator) => {
  // สุ่มเลข 1-9
  const n1 = Math.floor(Math.random() * 9) + 1;
  const n2 = Math.floor(Math.random() * 9) + 1;
  return [String(n1), String(n2)];
};

// Component วงล้อ (Reel)
const Reel = React.memo(({ symbols, symbolType }) => (
  <div className="reel">
    {symbols.map((symbol, i) => (
      <div key={i} className="reel-symbol">
        <span className={`symbol-${symbolType}`}>{symbol}</span>
      </div>
    ))}
  </div>
));

// Component สำหรับนับถอยหลัง
const CountdownTimer = React.memo(({ countdown }) => (
  <div className="countdown">เวลาที่เหลือ: {countdown} วินาที</div>
));

// Component สำหรับจัดการ countdown
const CountdownManager = React.memo(({ 
  isActive, 
  onTimeUp, 
  onCountdownChange, 
  initialCountdown = 5 
}) => {
  const [countdown, setCountdown] = useState(initialCountdown);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && countdown > 0) {
      intervalRef.current = setInterval(() => {
        setCountdown(prev => {
          const newCount = prev - 1;
          onCountdownChange(newCount);
          if (newCount <= 0) {
            clearInterval(intervalRef.current);
            onTimeUp();
            return initialCountdown;
          }
          return newCount;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, onTimeUp, onCountdownChange, initialCountdown]);

  useEffect(() => {
    if (!isActive) {
      setCountdown(initialCountdown);
    }
  }, [isActive, initialCountdown]);

  return <CountdownTimer countdown={countdown} />;
});

// Component หลักของเกม
export function SlotGame({ userData, onUserUpdate, onStopInformationSound }) {
  const [isSpinning, setIsSpinning] = useState(false);
  const [showAnswerInput, setShowAnswerInput] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [userAnswer, setUserAnswer] = useState('');
  const [currentResult, setCurrentResult] = useState(null);
  const [slotStopped, setSlotStopped] = useState(false);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [timeoutTimer, setTimeoutTimer] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState(null);
  const [finalReels, setFinalReels] = useState([
    ['1', '1', '1'], // ช่องที่ 1: หมายเลข
    ['+', '+', '+'], // ช่องที่ 2: สัญลักษณ์
    ['1', '1', '1'], // ช่องที่ 3: หมายเลข
  ]);
  
  const [reels, setReels] = useState([
    ['1', '1', '1'], // ช่องที่ 1: หมายเลข
    ['+', '+', '+'], // ช่องที่ 2: สัญลักษณ์
    ['1', '1', '1'], // ช่องที่ 3: หมายเลข
  ]);

  // เพิ่ม state
  const [answerDisabled, setAnswerDisabled] = useState(false);

  // เพิ่ม ref สำหรับ audio
  const slotOpenAudioRef = useRef(null);
  const slotTurnAudioRef = useRef(null);
  const countdownAudioRef = useRef(null);
  // เสียงตอบถูก/ผิด
  const trueAudioRef = useRef(new Audio(TrueSound));
  const wrongAudioRef = useRef(new Audio(WrongSound));

  // ฟังก์ชันสำหรับอัปเดตคะแนนและสถิติผู้ใช้
  const updateUserScore = useCallback(async (scoreToAdd, isCorrect, currentRoundSpeed = 0) => {

    
    if (!userData?.username) {

      return;
    }

    try {
      const collectionName = userData.collection || "Student-history";
      const docId = userData.id || userData.username;
      
      const userRef = doc(db, collectionName, docId);
      const docSnap = await getDoc(userRef);

      let currentDbScore = 0;
      let currentTotal = 0;
      let currentCount = 0;
      let currentStep6 = 0;
      let currentSpeed = 0;

      if (docSnap.exists()) {
        const dbData = docSnap.data();
        currentDbScore = dbData.score || 0;
        currentTotal = dbData['step6-total'] || 0; 
        currentCount = dbData.count || 0;
        currentStep6 = dbData.step6 || 0;
        currentSpeed = dbData['step6-speed'] || 0;
      }

      const newScore = currentDbScore + scoreToAdd;
      const newTotal = currentTotal + 1;
      const newCount = currentCount + 1;
      
      // อัปเดต step6 เฉพาะเมื่อตอบถูก
      const newStep6 = isCorrect ? currentStep6 + 1 : currentStep6;

      // Calculate new speed
      let newSpeed = currentSpeed;
      if (isCorrect) {
        if (currentSpeed === 0) {
          newSpeed = currentRoundSpeed;
        } else {
          newSpeed = (currentRoundSpeed + currentSpeed) / 2;
        }
      }

      await setDoc(userRef, { 
        score: newScore,
        'step6-total': newTotal,
        count: newCount,
        step6: newStep6,
        'step6-speed': newSpeed
      }, { merge: true });
      
      // console.log('Stats updated successfully');
      
      // if (onUserUpdate) {
      //   onUserUpdate(userData.username);
      // }
    } catch (error) {
      console.error("Error updating user stats:", error);
      Swal.fire('เกิดข้อผิดพลาด', 'ไม่สามารถอัปเดตข้อมูลได้', 'error');
    }
  }, [userData, onUserUpdate]);



  // ไม่เล่นเสียงทันทีเมื่อ component mount
  useEffect(() => {
    return () => {
      if (slotOpenAudioRef.current) {
        slotOpenAudioRef.current.pause();
        slotOpenAudioRef.current.currentTime = 0;
      }
    };
  }, []);



  // ปรับ checkAnswer ให้รับ argument permanent
  const checkAnswer = useCallback(async (currentResult, scoreValue) => {
    // ซ่อนช่องคำตอบทันทีเพื่อให้ปุ่มหมุนแสดงขึ้นมา
    setShowAnswerInput(false);

    if (!currentResult) return;
    const [num1, operator, num2] = currentResult;
    let correctResult = 0;

    // คำนวณผลลัพธ์ที่ถูกต้อง
    const n1 = parseInt(num1);
    const n2 = parseInt(num2);
    
    switch (operator) {
      case '+':
        correctResult = n1 + n2;
        break;
      case '-':
        correctResult = n1 - n2;
        break;
      case '×':
        correctResult = n1 * n2;
        break;
      case '÷':
        correctResult = n1 / n2;
        break;
      default:
        correctResult = 0;
    }

    // ตรวจสอบคำตอบของผู้เล่น (ถ้าไม่มีคำตอบให้ถือว่าผิด)
    const userAnswerNum = parseInt(userAnswer) || 0;
    const isCorrect = userAnswerNum === correctResult;
    
    // คำนวณคะแนนที่ได้ (หมดเวลา = 0 คะแนน)
    const scoreEarned = 0;
    
    // เก็บข้อมูลผลลัพธ์
    const newResultData = {
      isCorrect,
      correctResult,
      userAnswer: userAnswerNum,
      scoreEarned
    };
    
    setResultData(newResultData);
    setShowResultModal(true);
    
    if (isCorrect) {
      // เล่นเสียงตอบถูก
      if (trueAudioRef.current) {
        trueAudioRef.current.pause();
        trueAudioRef.current.currentTime = 0;
        trueAudioRef.current.play().catch(() => {});
      }
    } else {
      // เล่นเสียงตอบผิด
      if (wrongAudioRef.current) {
        wrongAudioRef.current.pause();
        wrongAudioRef.current.currentTime = 0;
        wrongAudioRef.current.play().catch(() => {});
      }
    }
    
    // อัปเดตคะแนนในฐานข้อมูล
    // console.log('About to update stats:', { scoreEarned, isCorrect });
    // In checkAnswer (timeout), speed is effectively 0 because time is up, but user didn't answer correctly anyway usually.
    await updateUserScore(scoreEarned, isCorrect, 0);
    
    // แสดง SweetAlert ทันที
    Swal.fire({
      title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
      text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
      icon: isCorrect ? 'success' : 'error',
      showCloseButton: false,
      showConfirmButton: true,
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      if (onUserUpdate) {
        onUserUpdate(userData.username);
      }
    });
  }, [userAnswer, updateUserScore]);

  // ในฟังก์ชัน spin ให้แน่ใจว่าเสียงหยุดและ reset
  const spin = useCallback(() => {
    if (isSpinning) return;
    // หยุดเสียง information เมื่อกดปุ่มหมุน
    if (onStopInformationSound) {
      onStopInformationSound();
    }
    // เล่นเสียงหมุน
    if (slotTurnAudioRef.current) {
      slotTurnAudioRef.current.currentTime = 0;
      slotTurnAudioRef.current.play().catch(() => {});
    }

    // ยกเลิก timer เก่าถ้ามี
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
    }

    setIsSpinning(true);
    setSlotStopped(false);
    setUserAnswer('');
    setCountdown(5);
    setShowAnswerInput(false);
    setShowResultModal(false);
    setResultData(null);

    // สุ่ม operator ก่อน
    const operator = OPERATORS[Math.floor(Math.random() * OPERATORS.length)];
    
    // สร้างตัวเลขที่เหมาะสมตาม operator
    const [num1, num3] = generateAppropriateNumbers(operator);
    
    // สร้างผลลัพธ์สุดท้าย
    const finalResult = [
      num1, 
      operator, 
      num3, 
    ];
    
    setCurrentResult(finalResult);
    
    // สร้าง Reel แบบสุ่มสำหรับแอนิเมชัน
    const newReels = [
      generateReelSymbols(finalResult[0], 'number'),
      generateReelSymbols(finalResult[1], 'operator'),
      generateReelSymbols(finalResult[2], 'number'),
    ];
    setReels(newReels);

    // จัดการหลังหมุนเสร็จ (4 วินาที)
    setTimeout(() => {
      setIsSpinning(false);
      setSlotStopped(true);
      // เก็บ reel สุดท้ายที่หยุดแล้ว
      setFinalReels(newReels);
      
      // เริ่มนับถอยหลังหลังจาก slot หยุดหมุน
      setShowAnswerInput(true);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setShowAnswerInput(false);
            // ตรวจสอบว่าผู้ใช้ยังไม่ได้ตอบคำถาม
            checkAnswer(finalResult).catch(console.error);
            return 5;
          }
          return prev - 1;
        });
      }, 1000);
      
      setCountdownTimer(interval);
      
      // หยุดนับถอยหลังหลังจาก 7 วินาที
      const timeout = setTimeout(() => {
        clearInterval(interval);
        setShowAnswerInput(false);
        checkAnswer(finalResult).catch(console.error);
      }, 5000);
      
      setTimeoutTimer(timeout);

      // เล่นเสียง countdown ที่นี่
      if (countdownAudioRef.current) {
        countdownAudioRef.current.currentTime = 0;
        countdownAudioRef.current.play().catch(() => {});
      }
    }, 4000); 
    setAnswerDisabled(false); 
  }, [isSpinning, countdownTimer, timeoutTimer, checkAnswer, onStopInformationSound]);

  const handleSubmit = useCallback(async () => {
    if (currentResult) {
      if (countdownTimer) {
        clearInterval(countdownTimer);
        setCountdownTimer(null);
      }
      if (timeoutTimer) {
        clearTimeout(timeoutTimer);
        setTimeoutTimer(null);
      }
      
      // หยุดเสียงนับถอยหลังทันทีที่ตอบ
      if (countdownAudioRef.current) {
        countdownAudioRef.current.pause();
        countdownAudioRef.current.currentTime = 0;
      }

      const [num1, operator, num2] = currentResult;
      let correctResult = 0;

      const n1 = parseInt(num1);
      const n2 = parseInt(num2);
      
      switch (operator) {
        case '+':
          correctResult = n1 + n2;
          break;
        case '-':
          correctResult = n1 - n2;
          break;
        case '×':
          correctResult = n1 * n2;
          break;
        case '÷':
          correctResult = n1 / n2;
          break;
        default:
          correctResult = 0;
      }

      const userAnswerNum = parseInt(userAnswer);
      const isCorrect = userAnswerNum === correctResult;
      
      // คำนวณคะแนน (เต็ม 40 ในเวลา 7 วิ)
      // ใช้สูตรบัญญัติไตรยางศ์: (เวลาที่เหลือ / เวลาเต็ม) * คะแนนเต็ม
      const scoreEarned = isCorrect ? Math.round((countdown / 5) * 40) : 0;
      
      const newResultData = {
        isCorrect,
        correctResult,
        userAnswer: userAnswerNum,
        scoreEarned
      };
      
      setResultData(newResultData);
      setShowResultModal(true);
      setAnswerDisabled(true); 
      
      if (isCorrect) {
        if (trueAudioRef.current) {
          trueAudioRef.current.pause();
          trueAudioRef.current.currentTime = 0;
          trueAudioRef.current.play().catch(() => {});
        }
      } else {
        if (wrongAudioRef.current) {
          wrongAudioRef.current.pause();
          wrongAudioRef.current.currentTime = 0;
          wrongAudioRef.current.play().catch(() => {});
        }
      }
      
      const currentRoundSpeed = isCorrect ? (countdown / 5) * 100 : 0;
      await updateUserScore(scoreEarned, isCorrect, currentRoundSpeed);
      
      Swal.fire({
        title: isCorrect ? 'ถูกต้อง! 🎉' : 'ผิด! 😔',
        text: isCorrect ? `คุณได้ ${scoreEarned} คะแนน!` : `คำตอบที่ถูกต้องคือ ${correctResult} (ได้ 0 คะแนน)`,
        icon: isCorrect ? 'success' : 'error',
        showCloseButton: false,
        showConfirmButton: true,
        confirmButtonText: 'OK',
        allowOutsideClick: false,
        allowEscapeKey: false
      }).then(() => {
        setShowAnswerInput(false);
        if (onUserUpdate) {
          onUserUpdate(userData.username);
        }
      });
    }
  }, [currentResult, countdownTimer, timeoutTimer, userAnswer, checkAnswer, updateUserScore]);

  const displayReels = slotStopped ? finalReels : reels;

  return (
    <div className="slot-machine-3">
      <header className="game-header">
        <h1 className="game-title">เกมส์ศึกสูตรคูณ</h1>
      </header>
      <div className="reels-container">
        {displayReels.map((reelSymbols, i) => (
          <div key={`reel-${i}-${slotStopped}`} className="reel-wrapper">
             <div 
               className={`reel-inner ${isSpinning ? 'spinning' : 'stopped'}`}
                 style={{ 
                  animationDelay: `${i * 0.3}s`
               }} 
             >
                <Reel symbols={reelSymbols} symbolType={i === 1 ? 'operator' : 'number'} />
             </div>
          </div>
        ))}
      </div>
      <div className="controls">
        {showAnswerInput ? (
          <div className="answer-input-container">
            <div className="countdown">
              เวลาที่เหลือ: {countdown} วินาที <br/>
              <span style={{ fontSize: '0.8em', color: '#ffd700' }}>(คะแนนที่จะได้: {Math.round((countdown / 5) * 40)} คะแนน)</span>
            </div>
            <input
              type="number"
              inputMode="numeric"
              autoFocus
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              onKeyDown={(e) => {
                // ป้องกันตัวอักษรที่ไม่ใช่ตัวเลข (e, +, -, .)
                if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                  e.preventDefault();
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit().catch(console.error);
                }
              }}
              placeholder="ใส่คำตอบ..."
              className="answer-input"
              disabled={answerDisabled}
            />
            <button onClick={() => handleSubmit().catch(console.error)} className="submit-button" disabled={answerDisabled}>
              ตอบคำถาม
            </button>
          </div>
        ) : (
          <button onClick={spin} disabled={isSpinning} className="spin-button">
            {isSpinning ? 'กำลังหมุน...' : 'หมุน'}
          </button>
        )}

      </div>
      <audio ref={slotOpenAudioRef} src={SlotgameOpen} preload="auto" loop />
      <audio ref={slotTurnAudioRef} src={SlotTurn} preload="auto" />
      <audio ref={countdownAudioRef} src={CountdownSound} preload="auto" />
    </div>
  );
}


